/**
 * Freigabeliste der bearbeitbaren Texte der oeffentlichen KAC-Website (AgoraHub-Bearbeitungsmodus, Plan W4).
 *
 * - Website-Texte: Die Uebersetzungs-Schluessel in `public/locales/{de,en}/translation.json` SIND die Kennungen
 *   (`website_texts.entries`). Nur Schluessel aus dieser Liste duerfen ueberschrieben werden; Dienstplan, Lernen und
 *   alle anderen nicht-oeffentlichen Uebersetzungen bleiben aussen vor.
 * - Ueber-uns-Texte (`about_us:<pfad>`): eine zentrale Wahrheit mit der App. Pfade wie in
 *   `agora_site_texts.ABOUT_US_EDIT_SCHEMA` (AgoraHub-Layer); der eingebaute Rueckfall liegt in
 *   `about_us_builtin.<pfad>` der Uebersetzungen und gilt nur, wenn das Feld in AgoraHub leer ist.
 *
 * `page`: 'shared' = auf jeder Seite (Menue, Fusszeile), sonst die Seite. `mode`: lines | paragraphs (Standard text).
 * `list: true` = Liste in den Uebersetzungen; Kennungen `<key>.0`, `<key>.1`, ... (Anzahl aus dem eingebauten Text).
 */

/** Kennung wie im Server (agora_site_texts.TEXT_KEY_RE). */
export const TEXT_KEY_RE = /^[a-z0-9][a-z0-9_.-]{0,79}$/;
export const ABOUT_PREFIX = 'about_us:';

export const PAGE_BY_PATH = {
  '/': 'home',
  '/about': 'about',
  '/opfer': 'giving',
  '/gebet': 'prayer',
  '/support': 'support',
  '/privacy': 'privacy',
};

const T = (page, key, label, extra) => ({ page, key, label, ...(extra || {}) });

const meta = (page, name) => [
  T(page, `meta.${page}.title`, `${name}: Seitentitel (Browser-Tab)`),
  T(page, `meta.${page}.description`, `${name}: Beschreibung für Suchmaschinen`),
];

const PRIVACY_PROCESSING = [
  ['hosting', 'Hosting'], ['login', 'Anmeldung'], ['prayers', 'Gebetsanliegen'], ['notifications', 'Push-Nachrichten'],
  ['profiles', 'Profile'], ['attendance', 'Anwesenheit'], ['lms', 'Lernen'], ['local_storage', 'Lokale Speicherung'],
  ['maps', 'Google Maps'],
];
export const PRIVACY_PROCESSING_KEYS = PRIVACY_PROCESSING.map(([key]) => key);

/** Reihenfolge der Wappen-Symbole (Unterzeile je Punkt; der Punkt selbst ist `about_us:logo_points.<n>.text`). */
export const CREST_SYMBOLS = ['trinity', 'pastor_teacher', 'apostle', 'prophet', 'evangelist'];
const CREST_NAMES = ['Dreieinigkeit', 'Pastor & Lehrer', 'Apostel', 'Prophet', 'Evangelist'];

export const WEBSITE_TEXTS = [
  // ---- Auf jeder Seite: Menue (Desktop + Handy), Fusszeile, Name der Website im Browser-Tab
  T('shared', 'nav.brand', 'Menü: Name neben dem Logo'),
  T('shared', 'nav.home', 'Menü: Startseite'),
  T('shared', 'nav.about', 'Menü: Über uns'),
  T('shared', 'nav.giving', 'Menü: Spenden'),
  T('shared', 'nav.prayer', 'Menü: Gebetsanliegen'),
  T('shared', 'nav.menu', 'Handy-Menü: Überschrift'),
  T('shared', 'nav.menu_open', 'Handy-Menü: Knopf „öffnen“ (Vorlesetext)'),
  T('shared', 'nav.menu_close', 'Handy-Menü: Knopf „schließen“ (Vorlesetext)'),
  T('shared', 'nav.main_menu', 'Handy-Menü: Name für Vorleseprogramme'),
  T('shared', 'footer.copyright', 'Fußzeile: Copyright'),
  T('shared', 'footer.founded', 'Fußzeile: Gründungsdatum'),
  T('shared', 'footer.support', 'Fußzeile: Link Support'),
  T('shared', 'footer.privacy', 'Fußzeile: Link Datenschutz'),
  T('shared', 'meta.site_name', 'Seitentitel: Name der Website (hinter jedem Seitentitel)'),

  // ---- Startseite
  ...meta('home', 'Startseite'),
  T('home', 'home.logo_title', 'Startseite: Name neben dem Wappen'),
  T('home', 'home.logo_subtitle', 'Startseite: Zeile unter dem Namen'),
  T('home', 'home.welcome_to', 'Startseite: Begrüßung über der Überschrift'),
  T('home', 'home.title', 'Startseite: Überschrift'),
  T('home', 'home.tagline', 'Startseite: Einleitungssatz'),
  T('home', 'home.day.sunday', 'Startseite: Wochentag Sonntag'),
  T('home', 'home.day.thursday', 'Startseite: Wochentag Donnerstag'),
  T('home', 'home.slide_caption', 'Startseite: Bildunterschrift der Fotos'),
  T('home', 'home.slide_label', 'Startseite: Fotos – Vorlesetext der Punkte'),
  T('home', 'home.events_kicker', 'Startseite: Termine – kleine Zeile'),
  T('home', 'home.upcoming_events', 'Startseite: Termine – Überschrift'),
  T('home', 'home.visit_kicker', 'Startseite: Standort – kleine Zeile'),
  T('home', 'home.visit_us', 'Startseite: Standort – Überschrift'),
  T('home', 'home.visit_text', 'Startseite: Standort – Einladungstext'),
  T('home', 'home.plan_route', 'Startseite: Knopf „Route planen“'),
  T('home', 'home.contact', 'Startseite: Knopf „Kontakt“'),
  T('home', 'map.consent', 'Karte: Hinweis vor dem Laden'),
  T('home', 'map.load', 'Karte: Knopf „Karte laden“'),
  T('home', 'map.open', 'Karte: Link „In Google Maps öffnen“'),
  T('home', 'map.title', 'Karte: Name für Vorleseprogramme'),

  // ---- Ueber uns (nur Website-Texte; die Inhalte selbst stehen in ABOUT_US_FIELDS)
  ...meta('about', 'Über uns'),
  T('about', 'about.leadership_heading', 'Über uns: Leitung – Überschrift'),
  T('about', 'about.show_more', 'Über uns: Knopf „Mehr lesen“'),
  T('about', 'about.show_less', 'Über uns: Knopf „Weniger anzeigen“'),
  T('about', 'about.vision_kicker', 'Über uns: Vision – kleine Zeile'),
  T('about', 'about.values_kicker', 'Über uns: Werte – kleine Zeile'),
  T('about', 'about.crest_heading', 'Über uns: Wappen – Überschrift'),
  T('about', 'about.crest_alt', 'Über uns: Wappen – Bildbeschreibung'),
  ...CREST_SYMBOLS.map((symbol, i) => T('about', `about.crest.${symbol}`, `Über uns: Wappen – Bedeutung „${CREST_NAMES[i]}“`)),

  // ---- Spenden
  ...meta('giving', 'Spenden'),
  T('giving', 'giving.eyebrow', 'Spenden: kleine Zeile über der Überschrift'),
  T('giving', 'giving.title', 'Spenden: Überschrift'),
  T('giving', 'giving.description', 'Spenden: Einleitung'),
  T('giving', 'giving.methods_kicker', 'Spenden: Wege – kleine Zeile'),
  T('giving', 'giving.methods_title', 'Spenden: Wege – Überschrift'),
  T('giving', 'giving.paypal.kicker', 'Spenden: PayPal – kleine Zeile'),
  T('giving', 'giving.paypal.title', 'Spenden: PayPal – Überschrift'),
  T('giving', 'giving.paypal.description', 'Spenden: PayPal – Text'),
  T('giving', 'giving.paypal.button', 'Spenden: PayPal – Knopf'),
  T('giving', 'giving.bank.kicker', 'Spenden: Überweisung – kleine Zeile'),
  T('giving', 'giving.bank.copy_hint', 'Spenden: Überweisung – Hinweis zum Kopieren'),
  T('giving', 'giving.bank.title', 'Spenden: Überweisung – Überschrift'),
  T('giving', 'giving.bank.inhaber', 'Spenden: Bezeichnung „Kontoinhaber“'),
  T('giving', 'giving.bank.iban', 'Spenden: Bezeichnung „IBAN“'),
  T('giving', 'giving.bank.bic', 'Spenden: Bezeichnung „BIC“'),
  T('giving', 'giving.bank.zweck', 'Spenden: Bezeichnung „Verwendungszweck“'),
  T('giving', 'giving.bank.copy', 'Spenden: Knopf „Kopieren“'),
  T('giving', 'giving.bank.copied', 'Spenden: Meldung „Kopiert“'),

  // ---- Gebetsanliegen
  ...meta('prayer', 'Gebet'),
  T('prayer', 'prayer.title', 'Gebet: Überschrift'),
  T('prayer', 'prayer.description', 'Gebet: Einleitung'),
  T('prayer', 'prayer.verse', 'Gebet: Bibelvers'),
  T('prayer', 'prayer.verse_reference', 'Gebet: Bibelstelle'),
  T('prayer', 'prayer.name_label', 'Gebet: Feld Name – Bezeichnung'),
  T('prayer', 'prayer.name_placeholder', 'Gebet: Feld Name – Platzhalter'),
  T('prayer', 'prayer.anonymous', 'Gebet: Kästchen „anonym“'),
  T('prayer', 'prayer.request_label', 'Gebet: Feld Anliegen – Bezeichnung'),
  T('prayer', 'prayer.request_placeholder', 'Gebet: Feld Anliegen – Platzhalter'),
  T('prayer', 'prayer.contact', 'Gebet: Kästchen „Kontaktaufnahme“'),
  T('prayer', 'prayer.phone_placeholder', 'Gebet: Feld Telefon – Platzhalter'),
  T('prayer', 'prayer.submit', 'Gebet: Knopf „Absenden“'),
  T('prayer', 'prayer.sending', 'Gebet: Meldung „Wird gesendet“'),
  T('prayer', 'prayer.success', 'Gebet: Meldung nach dem Absenden'),
  T('prayer', 'prayer.thanks', 'Gebet: Danke-Überschrift nach dem Absenden'),
  T('prayer', 'prayer.again', 'Gebet: Knopf „Weiteres Anliegen senden“'),
  T('prayer', 'prayer.error.empty', 'Gebet: Fehlermeldung „Anliegen fehlt“'),
  T('prayer', 'prayer.error.failed', 'Gebet: Fehlermeldung „Senden fehlgeschlagen“'),

  // ---- Support + Impressum
  ...meta('support', 'Support'),
  T('support', 'support.eyebrow', 'Support: kleine Zeile über der Überschrift'),
  T('support', 'support.title', 'Support: Überschrift'),
  T('support', 'support.description', 'Support: Einleitung'),
  ...[['contact', 'Technik'], ['privacy', 'Datenschutz'], ['help', 'Hilfe']].flatMap(([card, name]) => [
    T('support', `support.${card}.tag`, `Support: Karte ${name} – Etikett`),
    T('support', `support.${card}.title`, `Support: Karte ${name} – Überschrift`),
    T('support', `support.${card}.description`, `Support: Karte ${name} – Text`),
    T('support', `support.${card}.button`, `Support: Karte ${name} – Link`),
  ]),
  T('support', 'support.help.mail_subject', 'Support: Karte Hilfe – Betreff der E-Mail'),
  T('support', 'support.organization.title', 'Impressum: kleine Zeile'),
  T('support', 'support.organization.name', 'Impressum: Name'),
  T('support', 'support.organization.country', 'Impressum: Land'),
  T('support', 'support.organization.email_label', 'Impressum: Bezeichnung „E-Mail“'),
  T('support', 'support.organization.representative_label', 'Impressum: Bezeichnung „Vertreten durch“'),
  T('support', 'support.organization.representative_name', 'Impressum: Vertreten durch'),
  T('support', 'support.organization.responsible_label', 'Impressum: Bezeichnung „Verantwortlich“'),
  T('support', 'legal.imprint.address', 'Impressum: Anschrift (leer = Adresse aus AgoraHub-Kontakt)', { mode: 'lines' }),
  T('support', 'legal.imprint.register', 'Impressum: Registereintrag (leer = ausgeblendet)'),
  T('support', 'legal.imprint.responsible', 'Impressum: Verantwortlich für den Inhalt (leer = ausgeblendet)', { mode: 'lines' }),

  // ---- Datenschutz
  ...meta('privacy', 'Datenschutz'),
  T('privacy', 'privacy.eyebrow', 'Datenschutz: kleine Zeile über der Überschrift'),
  T('privacy', 'privacy.title', 'Datenschutz: Überschrift'),
  T('privacy', 'privacy.description', 'Datenschutz: Einleitung'),
  T('privacy', 'privacy.last_updated', 'Datenschutz: Bezeichnung „Stand“'),
  T('privacy', 'privacy.last_updated_date', 'Datenschutz: Stand (Datum)'),
  T('privacy', 'privacy.controller.title', 'Datenschutz: Verantwortlicher – Überschrift'),
  T('privacy', 'privacy.controller.name', 'Datenschutz: Verantwortlicher – Name'),
  T('privacy', 'privacy.controller.address', 'Datenschutz: Verantwortlicher – Anschrift'),
  T('privacy', 'privacy.controller.contact', 'Datenschutz: Verantwortlicher – Kontakt'),
  T('privacy', 'privacy.controller.representative', 'Datenschutz: Verantwortlicher – Vertretung'),
  T('privacy', 'privacy.data_types.title', 'Datenschutz: Erhobene Daten – Überschrift'),
  T('privacy', 'privacy.data_types.list', 'Datenschutz: Erhobene Daten – Punkt', { list: true }),
  T('privacy', 'privacy.legal_basis.title', 'Datenschutz: Rechtsgrundlagen – Überschrift'),
  T('privacy', 'privacy.legal_basis.list', 'Datenschutz: Rechtsgrundlagen – Punkt', { list: true }),
  T('privacy', 'privacy.rights.title', 'Datenschutz: Ihre Rechte – Überschrift'),
  T('privacy', 'privacy.rights.list', 'Datenschutz: Ihre Rechte – Punkt', { list: true }),
  T('privacy', 'privacy.processing.title', 'Datenschutz: Datenverarbeitung – Überschrift'),
  ...PRIVACY_PROCESSING.flatMap(([key, name]) => [
    T('privacy', `privacy.processing.${key}.title`, `Datenschutz: ${name} – Überschrift`),
    T('privacy', `privacy.processing.${key}.text`, `Datenschutz: ${name} – Text`),
  ]),
  T('privacy', 'privacy.storage.title', 'Datenschutz: Speicherdauer – Überschrift'),
  T('privacy', 'privacy.storage.list', 'Datenschutz: Speicherdauer – Punkt', { list: true }),
  T('privacy', 'privacy.contact.title', 'Datenschutz: Kontakt – Überschrift'),
  T('privacy', 'privacy.contact.dpo', 'Datenschutz: Kontakt – Ansprechpartner'),
  T('privacy', 'privacy.contact.email', 'Datenschutz: Kontakt – E-Mail'),
  T('privacy', 'privacy.contact.deletion', 'Datenschutz: Kontakt – Löschung'),
  T('privacy', 'privacy.extra.title', 'Datenschutz: Ergänzungen – Überschrift'),
  T('privacy', 'privacy.extra.text', 'Datenschutz: Ergänzungen (leer = ausgeblendet)', { mode: 'paragraphs' }),
  T('privacy', 'privacy.contact_us.title', 'Datenschutz: Anfrage – Überschrift'),
  T('privacy', 'privacy.contact_us.description', 'Datenschutz: Anfrage – Text'),
  T('privacy', 'privacy.contact_us.email.label', 'Datenschutz: Anfrage – Bezeichnung „E-Mail“'),
  T('privacy', 'privacy.contact_us.email.address', 'Datenschutz: Anfrage – E-Mail-Adresse'),
  T('privacy', 'privacy.contact_us.subject.label', 'Datenschutz: Anfrage – Bezeichnung „Betreff“'),
  T('privacy', 'privacy.contact_us.subject.text', 'Datenschutz: Anfrage – Betreff'),
  T('privacy', 'privacy.contact_us.button', 'Datenschutz: Anfrage – Knopf'),
];

/**
 * Bearbeitbare Textfelder von `about_us` — Kopie von `agora_site_texts.ABOUT_US_EDIT_SCHEMA` (AgoraHub,
 * lambda-layers/agora-auth-layer/python/agora_site_texts.py). 'str' = ein Text, 'paras' = Liste von Absaetzen,
 * [{...}] = Liste (Pfad ueber `position` aus der Public API).
 */
export const ABOUT_US_SCHEMA = {
  navigation_title: 'str',
  hero: { eyebrow: 'str', title: 'str', subtitle: 'str', paragraphs: 'paras' },
  intro: { welcome: 'str', title: 'str', body: 'str' },
  vision: { title: 'str', paragraphs: 'paras' },
  network: { title: 'str', paragraphs: 'paras' },
  leadership_story: { title: 'str', paragraphs: 'paras' },
  leadership: {
    global: {
      title: 'str', subtitle: 'str',
      pastor: { name: 'str', bio: 'str' },
      first_lady: { name: 'str', bio: 'str' },
    },
    local_leadership: { title: 'str', subtitle: 'str', name: 'str', pastor_bio: 'str' },
  },
  key_scripture: { title: 'str', reference: 'str', text: 'str' },
  vision_cards: [{ title: 'str', text: 'str' }],
  values: [{ title: 'str', description: 'str' }],
  logo_points: [{ text: 'str' }],
  sections: { vision_title: 'str', values_title: 'str', logo_title: 'str' },
  campus_highlights: { title: 'str', items: [{ title: 'str', subtitle: 'str', paragraphs: 'paras' }] },
  gallery: { title: 'str', items: [{ caption: 'str' }] },
};

/**
 * Ueber-uns-Felder, die die Website zeigt (Seite „Über uns“, Desktop und Handy). `#` = Stelle in der Liste.
 * Reihenfolge = Reihenfolge auf der Seite (Seitenleiste „Texte dieser Seite“).
 */
export const ABOUT_US_FIELDS = [
  { path: 'hero.eyebrow', label: 'Über uns: kleine Zeile über der Überschrift' },
  { path: 'hero.title', label: 'Über uns: Überschrift' },
  { path: 'hero.paragraphs', label: 'Über uns: Einleitung' },
  { path: 'leadership_story.title', label: 'Über uns: Leitung – kleine Zeile' },
  { path: 'leadership.global.title', label: 'Über uns: Leitung KAC Global – kleine Zeile' },
  { path: 'leadership.global.subtitle', label: 'Über uns: Leitung KAC Global – Namen' },
  { path: 'leadership.global.pastor.name', label: 'Über uns: Pastor – Name' },
  { path: 'leadership.global.pastor.bio', label: 'Über uns: Pastor – Lebenslauf (Leerzeile = neuer Absatz)' },
  { path: 'leadership.global.first_lady.name', label: 'Über uns: First Lady – Name' },
  { path: 'leadership.global.first_lady.bio', label: 'Über uns: First Lady – Lebenslauf (Leerzeile = neuer Absatz)' },
  { path: 'leadership.local_leadership.title', label: 'Über uns: Leitung KAC Berlin – kleine Zeile' },
  { path: 'leadership.local_leadership.subtitle', label: 'Über uns: Leitung KAC Berlin – Name' },
  { path: 'leadership.local_leadership.pastor_bio', label: 'Über uns: Leitung KAC Berlin – Lebenslauf (Leerzeile = neuer Absatz)' },
  { path: 'key_scripture.title', label: 'Über uns: Leitvers – Überschrift' },
  { path: 'key_scripture.reference', label: 'Über uns: Leitvers – Bibelstelle' },
  { path: 'key_scripture.text', label: 'Über uns: Leitvers – Text' },
  { path: 'sections.vision_title', label: 'Über uns: Vision – Überschrift' },
  { path: 'vision_cards.#.title', label: 'Über uns: Vision {n} – Titel', list: 'vision_cards' },
  { path: 'vision_cards.#.text', label: 'Über uns: Vision {n} – Text', list: 'vision_cards' },
  { path: 'sections.values_title', label: 'Über uns: Werte – Überschrift' },
  { path: 'values.#.title', label: 'Über uns: Wert {n} – Name', list: 'values' },
  { path: 'values.#.description', label: 'Über uns: Wert {n} – Beschreibung', list: 'values' },
  { path: 'sections.logo_title', label: 'Über uns: Wappen – kleine Zeile' },
  { path: 'logo_points.#.text', label: 'Über uns: Wappen – Punkt {n}', list: 'logo_points' },
];

const TEXT_BY_KEY = new Map(WEBSITE_TEXTS.map(entry => [entry.key, entry]));
const LIST_TEXTS = WEBSITE_TEXTS.filter(entry => entry.list);

/** Eintrag der Freigabeliste zu einer Kennung (auch Listen-Punkte `<key>.<n>`), sonst null. */
export function websiteTextEntry(key) {
  if (typeof key !== 'string' || !TEXT_KEY_RE.test(key)) return null;
  const exact = TEXT_BY_KEY.get(key);
  if (exact) return exact.list ? null : exact;
  const list = LIST_TEXTS.find(entry => key.startsWith(`${entry.key}.`) && /^\d{1,2}$/.test(key.slice(entry.key.length + 1)));
  return list ? { ...list, key, index: Number(key.slice(list.key.length + 1)) } : null;
}

/** Feldart eines Ueber-uns-Pfads ('str' | 'paras') oder null, wenn der Pfad nicht im Vertrag steht. */
export function aboutFieldKind(path) {
  if (typeof path !== 'string' || !path || path.length > 120) return null;
  let schema = ABOUT_US_SCHEMA;
  const parts = path.split('.');
  for (let i = 0; i < parts.length; i += 1) {
    const part = parts[i];
    if (Array.isArray(schema)) {
      if (!/^\d{1,2}$/.test(part)) return null;
      [schema] = schema;
    } else if (schema && typeof schema === 'object') {
      if (!Object.prototype.hasOwnProperty.call(schema, part)) return null;
      schema = schema[part];
    } else {
      return null;
    }
  }
  return schema === 'str' || schema === 'paras' ? schema : null;
}

/** Ueber-uns-Feld der Website zu einem Pfad (Listen-Stelle wird zu `#`), sonst null. */
export function aboutFieldEntry(path) {
  const pattern = String(path || '').replace(/\.\d{1,2}(?=\.|$)/, '.#');
  const entry = ABOUT_US_FIELDS.find(field => field.path === pattern);
  if (!entry) return null;
  const match = /\.(\d{1,2})(?=\.|$)/.exec(String(path));
  return { ...entry, path, index: match ? Number(match[1]) : null };
}

/** Deutsche Beschriftung fuer den Bearbeitungsmodus („Startseite: Überschrift“). */
export function labelForKey(key) {
  if (typeof key === 'string' && key.startsWith(ABOUT_PREFIX)) {
    const entry = aboutFieldEntry(key.slice(ABOUT_PREFIX.length));
    if (!entry) return '';
    return entry.label.replace('{n}', entry.index == null ? '' : String(entry.index + 1));
  }
  const entry = websiteTextEntry(key);
  if (!entry) return '';
  return entry.index == null ? entry.label : `${entry.label} ${entry.index + 1}`;
}
